import { GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
} from '~/components/SolaForceDataGrid';
import { PersonnelReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import {
  convertToDateOrUndefined,
  convertToMomentOrUndefined,
} from '~/utils/helpers/date';

export type PersonnelReportTableRow = {
  employeeId: number;
  name: string;
  unitName: string;
  startDate?: Date;
  age?: number;
};

export const usePersonnelReportTables = (
  data: PersonnelReportData[],
  omitColumns?: string[],
) => {
  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const rows: PersonnelReportTableRow[] = useMemo(() => {
    return data.map((dataEntry) => {
      const {
        fEmpId,
        fEmpFirstName,
        fEmpLastName,
        fEmpUnit,
        fEmpStartDate,
        fEmpDOB,
      } = dataEntry;

      const dob = convertToMomentOrUndefined(fEmpDOB);

      return {
        employeeId: fEmpId,
        name: `${fEmpFirstName || ''} ${fEmpLastName || ''}`,
        unitName: fEmpUnit,
        startDate: convertToDateOrUndefined(fEmpStartDate),
        age: !!dob ? moment().diff(dob, 'years') : undefined,
      };
    });
  }, [data]);

  const columns = useMemo((): GridColDef<PersonnelReportTableRow>[] => {
    const columns: GridColDef<PersonnelReportTableRow>[] = [
      {
        field: 'employeeId',
        headerName: '',
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        width: 40,
        minWidth: 30,
      },
      {
        field: 'name',
        headerName: t('laName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 140,
      },
      {
        field: 'unitName',
        headerName: t('laOrgUnit'),
        minWidth: 140,
      },
      {
        field: 'startDate',
        headerName: t('laStartDate'),
        type: 'date',
        valueFormatter: dateCellValueFormatter,
        minWidth: 110,
      },
      {
        field: 'age',
        headerName: t('laAge'),
        type: 'number',
        minWidth: 90,
      },
    ];

    return columns.filter((c) =>
      !!omitColumns && omitColumns.length > 0
        ? !omitColumns.includes(c.field)
        : true,
    );
  }, [t, goToEmployeeBasicDataView, omitColumns]);

  return {
    rows,
    columns,
  };
};
